import PropTypes from 'prop-types';
import React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import dot from 'dot-object';
import classNames from 'classnames';
import autocast from 'autocast';
import * as Sentry from '@sentry/react';
import _ from 'lodash';

import { fetchHomeFavorites } from '../../../src/actions/homeFavorites';
import { fetchSavedSearches } from '../../../src/actions/savedSearches';

import Api from '../../api/Api';
import i18n from '../../i18n';
import Utils from '../../utils/utils';
import { boundsMatch, findExistingAlert } from '../../utils/alertComparison';
import FiltersModel from '../../models/Filters';
import Group from '../../models/Group';
import Home from '../../models/Home';
import User from '../../models/User';
import { setDisplayAlertsComponentStatus } from '../../actions/search';
import { addSearch } from '../../actions/savedSearches';
import Screen from '../../utils/Screen';
import { SearchMap } from './map-ts';
import { PROVIDER_JAWG } from '../../constants/search';
import AutocompleteService from '../../utils/AutocompleteService';
import { isMapAreaFilters } from '../../utils/mapAreaFilters';

import Filters from './Filters';
import OfflineFilters from './OfflineFilters';
import { Results, HomeLoading, NoResults, RecommendationsCarousel } from './results-ts';
import { Pagination, Text, Flex, Button, MapPin, Icon } from '@homeexchange/design';

export class Search extends React.Component {
    filtersChanged = false;

    static propTypes = {
        user: PropTypes.instanceOf(User),
        groups: PropTypes.arrayOf(PropTypes.instanceOf(Group)),
        alerts: PropTypes.arrayOf(PropTypes.object),
        queryString: PropTypes.string,
        placeId: PropTypes.string,
        filters: PropTypes.object,
        offset: PropTypes.number,
        resultsCount: PropTypes.number,
        results: PropTypes.arrayOf(PropTypes.instanceOf(Home)),
        bounds: PropTypes.shape({
            ne: PropTypes.shape({ lat: PropTypes.number, lon: PropTypes.number }),
            sw: PropTypes.shape({ lat: PropTypes.number, lon: PropTypes.number })
        }),
        search: PropTypes.func.isRequired,
        isLogged: PropTypes.bool,
        searchPage: PropTypes.func.isRequired,
        getNumResultsPerPage: PropTypes.func.isRequired,
        getQuery: PropTypes.func.isRequired,
        additionalSearch: PropTypes.shape({
            offset: PropTypes.number,
            results: PropTypes.arrayOf(PropTypes.instanceOf(Home)),
            resultsCount: PropTypes.number,
            bounds: PropTypes.shape({
                ne: PropTypes.shape({ lat: PropTypes.number, lon: PropTypes.number }),
                sw: PropTypes.shape({ lat: PropTypes.number, lon: PropTypes.number })
            })
        }),
        lastSearchId: PropTypes.number,
        displaySearchInThisRegionBtn: PropTypes.bool,
        updateDisplaySearchInThisRegionBtnStatus: PropTypes.func,
        displayAlerts: PropTypes.bool,
        updateDisplayAlertsStatus: PropTypes.func,
        addAlert: PropTypes.func,
        fetchHomeFavorites: PropTypes.func,
        fetchSavedSearches: PropTypes.func,
        homeFavorites: PropTypes.arrayOf(PropTypes.number),
        accessToken: PropTypes.string,
        dispatch: PropTypes.func
    };

    static defaultProps = {
        queryString: '',
        placeId: null,
        filters: {},
        offset: 0
    };

    static MAX_RESULTS_TO_SHOW_ADDITIONAL_SEARCH = 50;
    static MAIN_RESULTS = 'MAIN_RESULTS';
    static ADDITIONAL_RESULTS = 'ADDITIONAL_RESULTS';

    constructor(props) {
        super(props);

        this.page = 1;
        this.filterUpdateInProgress = false;
        this.boundsFromMap = false;
        this.lastMapBounds = null;
        // The map mounts fitted on these bounds; flying to them again would only animate the zoom.
        this.lastFramedBounds = isMapAreaFilters(props.filters) ? props.filters.location.bounds : null;

        this.initSearch = this.initSearch.bind(this);
        this.search = this.search.bind(this);
        this.searchPage = this.searchPage.bind(this);
        this.updateFilters = this.updateFilters.bind(this);
        this.homeRollOut = this.homeRollOut.bind(this);
        this.homeRollOver = this.homeRollOver.bind(this);
        this.gotoPage = this.gotoPage.bind(this);
        this.setBounds = this.setBounds.bind(this);
        this.shouldShowMainResults = this.shouldShowMainResults.bind(this);
        this.shouldShowAdditionalResults = this.shouldShowAdditionalResults.bind(this);
        this.onUpdatePage = this.onUpdatePage.bind(this);
        this.onSearchStart = this.onSearchStart.bind(this);
        this.onSearchSuccess = this.onSearchSuccess.bind(this);
        this.removeFilter = this.removeFilter.bind(this);
        this.shouldShowPush = this.shouldShowPush.bind(this);
        this.updateGpRange = this.updateGpRange.bind(this);
        this.handleClick = this.handleClick.bind(this);
        this.toggleMoreFiltersModalDisplay = this.toggleMoreFiltersModalDisplay.bind(this);
        this.searchMapRef = React.createRef();
        this.expandSearchArea = this.expandSearchArea.bind(this);
        this.showAllAdditionalResults = this.showAllAdditionalResults.bind(this);
        this.handleOnPushAlert = this.handleOnPushAlert.bind(this);
        this.fetchRecommendations = this.fetchRecommendations.bind(this);
        this.state = {
            placeId: props.placeId,
            gp_range: {
                min: 0,
                max: 250
            },
            openMoreFiltersModal: false,
            filters: null,
            hoveredHomeId: null,
            displayMap: false,
            loading: false,
            additionalLoading: false,
            screenState: {
                noDesktop: Screen.noDesktop(),
                isTabletOrUpper: Screen.isTabletOrUpper(),
                isLargeScreen: Screen.isLargeScreen()
            },
            showAllAdditionalResults: false,
            recommendations: null,
            recommendationsTotal: 0,
            recommendationsLoading: false,
            recommendationsSeeMore: ''
        };
    }

    componentDidMount() {
        $(document.body).addClass('search-body');

        if (this.props.user && this.props.user.get('is_he_collection')) {
            this.updateGpRange({ ...this.state.gp_range, max: 700 });
        }

        // header full width
        $('#head-nav-band > .navbar > .container').removeClass('container').addClass('container-fluid');

        // remove footer
        $('body > footer').remove();

        this.initSearch();

        // handle navigation event
        window.addEventListener('popstate', this.initSearch, false);

        // change page when changing filters in the searchbar
        document.addEventListener('update_page', this.onUpdatePage);
        // add loaders when search is processing
        document.addEventListener('search-start', this.onSearchStart);
        document.addEventListener('search-success', this.onSearchSuccess);

        if (this.props.isLogged && typeof this.props.fetchHomeFavorites === 'function') {
            this.props.fetchHomeFavorites();
        }

        if (this.props.isLogged && typeof this.props.fetchSavedSearches === 'function') {
            this.props.fetchSavedSearches();
        }

        this.removeResizeListener = Screen.addResizeListener((screenState) => {
            this.setState({ screenState });
        });

        this.debouncedRemoveListener = _.debounce(() => {
            this.removeResizeListener();
        }, 200);

        window.addEventListener('beforeunload', this.debouncedRemoveListener);
    }

    componentWillUnmount() {
        window.removeEventListener('popstate', this.initSearch, false);
        document.removeEventListener('update_page', this.onUpdatePage);
        document.removeEventListener('search-start', this.onSearchStart);
        document.removeEventListener('search-success', this.onSearchSuccess);
        window.removeEventListener('beforeunload', this.debouncedRemoveListener);
    }

    componentDidUpdate(prevProps, prevState) {
        this.saveState();
        this.updateUrl(prevProps, prevState);
    }

    componentDidCatch(error, info) {
        console.error(error, info);
        Sentry.withScope((scope) => {
            scope.setExtra('info', info);
            Sentry.captureException(error);
        });
    }

    UNSAFE_componentWillReceiveProps(nextProps) {
        if (this.props.filters && nextProps.filters != this.props.filters) {
            let nextFilters = nextProps.filters;
            this.setState({
                filters: this.stateFiltersFromProps(nextProps)
            });
            this.filtersChanged = true;

            if (this.filterUpdateInProgress) {
                this.filterUpdateInProgress = false;
                return;
            }

            if (this.searchbarFiltersHaveChanged(this.props.filters, nextFilters)) {
                this.updateFilters(nextFilters);
            }
        }
    }

    onUpdatePage(e) {
        if (e && e.detail && e.detail.page) {
            const pageChanged = this.page !== e.detail.page;
            this.page = e.detail.page;
            if (e.detail.forceSearch && pageChanged) {
                this.searchPage(this.props.filters, e.detail.page);
            }
        }
    }

    onSearchStart(e) {
        if (e && e.detail && e.detail.type) {
            if (e.detail.type === 'main') {
                this.setState({ loading: true });
            } else {
                this.setState({ additionalLoading: true });
            }
        }
    }

    onSearchSuccess(e) {
        if (e && e.detail && e.detail.type) {
            if (e.detail.type === 'main') {
                this.setState({ loading: false });
            } else {
                this.setState({ additionalLoading: false });
            }
            // Scroll to the top of the result list
            this.scrollable.scrollTop = 0;
        }
    }

    updateGpRange(gpRange) {
        this.setState({ gp_range: gpRange });
    }

    initSearch() {
        const { user } = this.props;
        const defaultFilters = {};
        const context = Utils.getParameter('context', null);

        this.page = parseInt(Utils.getParameter('page', 1), 10);

        return this.getFiltersFromUrl(defaultFilters)
            .then(this.generateLocationFilters.bind(this))
            .then((filters) => {
                if (
                    user &&
                    user.get('is_he_collection') &&
                    user.get('he_collection_version') >= 2 &&
                    context === 'search-bar'
                ) {
                    filters.luxury = true;
                }
                this.setState({ filters });
                return filters;
            })
            .then(this.updateFilters);
    }

    search(filters, page) {
        const { lastSearchId } = this.props;
        const { placeId } = filters;

        this.setState({
            loading: true,
            showAllAdditionalResults: false
        });

        const mapBoundsAtStart = this.lastMapBounds;
        const searchPromise = this.props.search(
            {
                filters,
                placeId,
                lastSearchId
            },
            page
        );

        if (page === 1 || page === undefined) {
            const hasDates =
                filters.calendar?.date_ranges &&
                filters.calendar.date_ranges.length > 0 &&
                filters.calendar.date_ranges[0]?.from &&
                filters.calendar.date_ranges[0]?.to;

            if (hasDates) {
                this.fetchRecommendations(filters);
            } else {
                this.setState({
                    recommendations: [],
                    recommendationsTotal: 0,
                    recommendationsLoading: false
                });
            }
        }

        if (searchPromise && typeof searchPromise.then === 'function') {
            searchPromise
                .then((results) => {
                    this.frameMap(filters, results, mapBoundsAtStart);
                    this.setState({ loading: false });
                    if (filters.extended) {
                        this.setState({ additionalLoading: false });
                    }

                    this.filterUpdateInProgress = false;
                })
                .catch(() => {
                    this.boundsFromMap = false;
                    this.filterUpdateInProgress = false;
                });
        } else {
            this.boundsFromMap = false;
            this.filterUpdateInProgress = false;
        }
    }

    // A pan/zoom already shows its own bounds: re-framing it would only jitter the map.
    frameMap(filters, results, mapBoundsAtStart) {
        const fromMapGesture = this.boundsFromMap;
        this.boundsFromMap = false;

        const map = this.searchMapRef.current;
        if (!map) {
            return;
        }
        // The member moved the map while this search was running: its results must not move it back.
        if (this.lastMapBounds !== mapBoundsAtStart) {
            return;
        }
        if (filters.location?.polygon?.location_id) {
            map.flyTo(results.bounds);
        } else if (
            isMapAreaFilters(filters) &&
            !fromMapGesture &&
            !boundsMatch(filters.location.bounds, this.lastMapBounds) &&
            !boundsMatch(filters.location.bounds, this.lastFramedBounds)
        ) {
            // Bounds already on screen (initial fit, previous frame or member gesture) must not be re-framed.
            map.flyTo(filters.location.bounds);
            this.lastFramedBounds = filters.location.bounds;
        }
    }

    searchPage(filters, page) {
        this.setState({ loading: true });

        const searchPagePromise = this.props.searchPage(
            {
                filters,
                placeId: filters.placeId
            },
            page
        );

        if (searchPagePromise && typeof searchPagePromise.then === 'function') {
            searchPagePromise.then(() => {
                this.setState({ loading: false });
                if (filters.extended) {
                    this.setState({ additionalLoading: false });
                }
            });
        }

        const target = Screen.isMobile() ? window : $('.search-panel')[0];
        target.scroll(0, 0);
    }

    getFiltersFromUrl(defaultFilters) {
        return new Promise((resolve) => {
            const filters = FiltersModel.fromUrl().toJSON();
            // Add some filters by default (if not already present)
            if (defaultFilters) {
                const defaults = dot.object(Utils.deepclone(defaultFilters));
                for (const filter in defaults) {
                    const defaultFilterValue = dot.pick(filter, defaults);
                    if (Array.isArray(defaultFilterValue)) {
                        // We want to extend arrays and not replace them
                        const value = dot.pick(filter, filters) || []; // Get current value
                        defaultFilterValue.forEach((val) => {
                            // Check that the default filter is not already in the filters
                            if (value.indexOf(val) < 0) {
                                // Add to filters
                                value.push(val);
                                dot.str(filter, value, filters);
                            }
                        });
                    } else {
                        // Just set the value
                        dot.str(filter, defaultFilterValue, filters);
                    }
                }
            }
            resolve(filters);
        });
    }

    async generateLocationFilters(filters) {
        // Generate bounds filter if not defined
        if (
            !_.has(filters, 'location') ||
            (!_.has(filters.location, 'bounds') && !_.has(filters.location, 'polygon'))
        ) {
            const queryString = this.props.getQuery();
            if (queryString) {
                const result = await AutocompleteService.getPlaceSuggestions(queryString);
                const firstResult = result[0];
                return Object.assign({}, filters, {
                    location: Object.assign({}, filters.location, {
                        polygon: {
                            location_id: firstResult.properties.gid,
                            provider: PROVIDER_JAWG,
                            ...(firstResult.properties.layer && {
                                layer: firstResult.properties.layer
                            })
                        },
                        country: firstResult.properties.country_a,
                        ...(firstResult.properties.name && { city: firstResult.properties.name }),
                        ...(firstResult.properties.country_code && {
                            country_code: firstResult.properties.country_code
                        })
                    })
                });
            } else {
                // set world bounds per default
                filters.location = Object.assign({}, filters.location, {
                    bounds: {
                        ne: {
                            lat: 85,
                            lon: 180
                        },
                        sw: {
                            lat: -85,
                            lon: -180
                        }
                    }
                });
                return Promise.resolve(filters);
            }
        } else {
            return Promise.resolve(filters);
        }
    }

    // utility method to update filters
    updateFilters(filters) {
        this.filterUpdateInProgress = true;
        filters = Object.assign({}, FiltersModel.getEmptyFilters(), filters);
        this.page = 1;
        this.search(filters);
    }

    setBounds(bounds) {
        // don't update bounds if map is hidden (bounds 0)
        if ($(this.mapContainer).is(':hidden')) {
            return;
        }
        if (
            _.has(this.props.filters, 'location') &&
            _.isEqual(bounds, this.props.filters.location.bounds) &&
            !this.filtersChanged
        ) {
            return;
        }
        // clone filters
        this.filtersChanged = false;
        const filters = JSON.parse(JSON.stringify(this.props.filters));
        filters.location = filters.location || {};
        filters.location.bounds = bounds;
        delete filters.location.polygon;
        delete filters.location.center;
        delete filters.location.city;
        // The drawn box is the whole area: the country of the previous named destination (ISO3) would
        // only make the search API return nothing.
        delete filters.country;
        delete filters.location.country;
        // update state
        this.page = 1;
        this.boundsFromMap = true;
        this.lastMapBounds = bounds;
        this.search(filters);
    }

    saveState() {
        if (this.props.user) {
            const data = {
                place: this.props.queryString,
                query: this.props.filters,
                offset: this.props.offset
            };
            Api.Search.setLastSearch(this.props.user.id, data);
        }
    }

    updateUrl(prevProps, prevState) {
        const cleanParamsForComparison = (params) => {
            const cleaned = { ...params };
            if (
                cleaned.place_id === false ||
                cleaned.place_id === 'false' ||
                cleaned.place_id === null ||
                cleaned.place_id === undefined
            ) {
                delete cleaned.place_id;
            }
            return cleaned;
        };

        const prevFilters = new FiltersModel(prevProps.filters);
        const filters = new FiltersModel(this.props.filters);

        const cleanedPrevParams = cleanParamsForComparison(prevFilters.params());
        const cleanedCurrentParams = cleanParamsForComparison(filters.params());

        if (
            prevState.placeId === this.state.placeId &&
            this.props.offset === prevProps.offset &&
            this.props.additionalSearch.offset === prevProps.additionalSearch.offset &&
            _.isEqual(cleanedPrevParams, cleanedCurrentParams)
        ) {
            // no changes
            return;
        }
        const queryString = this.props.queryString ? this.props.queryString : this.props.getQuery();
        const path = queryString
            ? i18n.t('search:search_url', {
                  slug: encodeURIComponent(queryString)
              })
            : document.location.pathname;

        let params = filters.params();
        if (this.page > 1) {
            params.page = this.page;
        }

        const currentParams = new URLSearchParams(window.location.search);
        const isCurrentlyRecommendation = currentParams.get('search_type') === 'recommendation';

        const filtersUnchanged = _.isEqual(cleanedPrevParams, cleanedCurrentParams);
        const offsetChanged =
            this.props.offset !== prevProps.offset ||
            this.props.additionalSearch.offset !== prevProps.additionalSearch.offset;
        const placeUnchanged = prevState.placeId === this.state.placeId;

        const isOnlyPagination = filtersUnchanged && offsetChanged && placeUnchanged;

        const isFirstLoad = !prevProps.filters || _.isEmpty(prevProps.filters);
        const isSameSearchWithRecommendation = filtersUnchanged && !offsetChanged && placeUnchanged;

        if (
            isCurrentlyRecommendation &&
            (isOnlyPagination || isFirstLoad || isSameSearchWithRecommendation)
        ) {
            params.search_type = 'recommendation';
        }

        params = $.param(params);
        const url = path + (params ? `?${params}` : '');

        const currentSearchParams = document.location.search.startsWith('?')
            ? document.location.search.substring(1)
            : document.location.search;

        if (currentSearchParams !== params) {
            if (
                prevState.placeId &&
                (!_.isEmpty(prevProps.filters) || this.props.offset != prevProps.offset)
            ) {
                history.pushState(null, null, url);
            } else {
                history.replaceState(null, null, url);
            }
        }
    }

    homeRollOver(id) {
        if (this.state.hoveredHomeId !== id) {
            this.setState({ hoveredHomeId: id });
        }
    }

    homeRollOut() {
        if (this.state.hoveredHomeId !== null) {
            this.setState({ hoveredHomeId: null });
        }
    }

    getNbPages() {
        const totalResults = this.props.resultsCount + this.props.additionalSearch.resultsCount;
        const nbPages = Math.ceil(totalResults / this.props.getNumResultsPerPage());
        return Math.min(nbPages, 200);
    }

    gotoPage(page) {
        this.page = page;

        this.searchPage(this.props.filters, page);
    }

    shouldShowMainResults(page = this.page) {
        return (
            _.isNull(this.props.resultsCount) ||
            this.props.resultsCount > (page - 1) * this.props.getNumResultsPerPage() ||
            (this.props.resultsCount === 0 &&
                (!this.shouldShowAdditionalResults() || this.props.additionalSearch.resultsCount === 0))
        );
    }

    shouldShowAdditionalResults(page = this.page) {
        return (
            !_.isNull(this.props.resultsCount) &&
            this.props.resultsCount < Search.MAX_RESULTS_TO_SHOW_ADDITIONAL_SEARCH &&
            this.props.resultsCount <= page * this.props.getNumResultsPerPage() &&
            (_.isNull(this.props.additionalSearch.resultsCount) || // Results not yet loaded
                this.props.additionalSearch.resultsCount > 0)
        );
    }

    shouldShowPush(origin) {
        const { resultsCount, user, filters } = this.props;

        if (this.getExistingAlert()) {
            return false;
        }

        if (this.page % 2 === 0 || !this.hasSavableLocation(filters)) {
            return false;
        }

        const shouldShowReferral =
            user && !user.get('is_he_collection') && user.get('has_finalized_exchange_in_last_30_days');

        if (shouldShowReferral) {
            return { type: 'referral' };
        }

        const shouldShowAlert = origin === Search.MAIN_RESULTS ? resultsCount > 0 : resultsCount === 0;

        if (shouldShowAlert) {
            return { type: 'alert' };
        }

        return false;
    }

    hasSavableLocation(filters = this.props.filters) {
        return Boolean(filters?.location?.polygon) || isMapAreaFilters(filters);
    }

    hasDates() {
        return Boolean(
            this.props.filters.calendar?.date_range &&
                this.props.filters.calendar.date_ranges.length > 0 &&
                this.props.filters.calendar.date_ranges[0].from &&
                this.props.filters.calendar.date_ranges[0].to
        );
    }

    toggleMoreFiltersModalDisplay() {
        this.setState({
            openMoreFiltersModal: !this.state.openMoreFiltersModal
        });
    }

    searchbarFiltersHaveChanged(oldFilters, newFilters) {
        if (_.isEmpty(oldFilters) || (!oldFilters && !newFilters)) {
            // No filters
            return false;
        } else if (!oldFilters && newFilters) {
            // Filters for the first time
            return true;
        } else if (JSON.stringify(oldFilters.home) !== JSON.stringify(newFilters.home)) {
            // Guest number has changed
            return true;
        } else if (
            (!oldFilters.location && newFilters.location) ||
            (oldFilters.location && !newFilters.location) ||
            (oldFilters.location &&
                newFilters.location &&
                (!_.isEqual(oldFilters.location.bounds, newFilters.location.bounds) ||
                    !_.isEqual(oldFilters.location.polygon, newFilters.location.polygon)))
        ) {
            return true;
        } else if (!_.isEqual(oldFilters.calendar, newFilters.calendar)) {
            // Dates have changed
            return true;
        }

        return false;
    }

    stateFiltersFromProps(props) {
        const filters = new FiltersModel(props.filters);
        return filters.flatify();
    }

    filtersFromState() {
        const filters = FiltersModel.fromFlatFilters(this.state.filters);
        filters.set('location', Object.assign({}, this.props.filters.location, filters.get('location')));
        filters.set('calendar', Object.assign({}, this.state.filters.calendar));
        return filters.toJSON();
    }

    removeFilter(filter, value) {
        let { filters } = this.state;

        if (Array.isArray(filters[filter]) && filter !== 'reverse') {
            const newFilter = {};
            value = _.isBoolean(value) ? value : autocast(value);
            newFilter[filter] = _.without(filters[filter], value);
            filters = Object.assign({}, filters, newFilter);
        } else if (['reciprocal', 'guest-wanted', 'available'].includes(value)) {
            const calendar = Object.assign({}, filters.calendar);
            calendar.exchange_types = _.without(calendar.exchange_types, value);
            filters = Object.assign({}, filters, { calendar });
        } else {
            delete filters[filter];
        }

        this.setState({ filters }, () => {
            this.updateFilters(this.filtersFromState());
        });
    }

    isMobileMapView() {
        return Screen.noDesktop() && this.state.displayMap;
    }

    getSearchPanelStyle() {
        return {
            display: this.isMobileMapView() ? 'none' : 'block'
        };
    }

    handleClick() {
        this.setState({
            displayMap: !this.state.displayMap
        });
    }

    expandSearchArea(scale = 1.5) {
        if (this.searchMapRef.current) {
            this.searchMapRef.current.unzoom(scale);
        }
    }

    showAllAdditionalResults() {
        this.setState({ showAllAdditionalResults: true });
    }

    handleOnPushAlert() {
        const filtersFromUrl = isMapAreaFilters(this.props.filters)
            ? this.props.filters
            : FiltersModel.fromUrl().toJSON();

        return new Promise((resolve, reject) => {
            if (!this.props.isLogged) {
                const event = new CustomEvent('open_signin_popup', {
                    detail: {
                        onLogin: () => {
                            this.props
                                .addAlert(this.props.queryString, filtersFromUrl)
                                .then((createdAlert) => {
                                    resolve(createdAlert);
                                })
                                .catch((error) => reject(error));
                        }
                    }
                });
                document.dispatchEvent(event);
            } else {
                this.props
                    .addAlert(this.props.queryString, filtersFromUrl)
                    .then((createdAlert) => {
                        resolve(createdAlert);
                    })
                    .catch((error) => reject(error));
            }
        });
    }

    getExistingAlert() {
        const alerts = this.props.alerts;
        const query = this.props.queryString;
        const filters = this.props.filters;

        return findExistingAlert(alerts, { query, filters });
    }

    isFlexibleSearch(filters) {
        if (!filters || !filters.calendar) {
            return null;
        }

        const hasMultipleDateRanges = filters.calendar.date_ranges && filters.calendar.date_ranges.length > 1;

        const hasDuration = filters.calendar.duration && filters.calendar.duration.kind;

        return hasMultipleDateRanges || hasDuration;
    }

    fetchRecommendations(filters) {
        const { lastSearchId } = this.props;
        const { placeId } = filters;

        this.setState({ recommendationsLoading: true });

        return Api.Search.getRecommendations(
            filters,
            this.props.queryString,
            placeId,
            lastSearchId,
            this.props.accessToken,
            this.props.dispatch
        )
            .then((results) => {
                if (results && results.homes && results.total) {
                    this.setState({
                        recommendations: results.homes.map((home) => new Home(home)),
                        recommendationsSeeMore:
                            '/search-v2' +
                            (this.props.queryString ? '/' + this.props.queryString : '') +
                            '?' +
                            results.see_more.queryParams,
                        recommendationsTotal: results.total,
                        recommendationsLoading: false
                    });
                } else {
                    this.setState({
                        recommendations: [],
                        recommendationsSeeMore: '',
                        recommendationsTotal: 0,
                        recommendationsLoading: false
                    });
                }
                return results;
            })
            .catch(() => {
                this.setState({
                    recommendations: [],
                    recommendationsSeeMore: '',
                    recommendationsTotal: 0,
                    recommendationsLoading: false
                });
            });
    }

    render() {
        const { bounds, additionalSearch, isLogged, queryString, results, user } = this.props;
        // A map-area URL must frame the member's area, not the bbox the server computed from the results.
        const initialBounds = isMapAreaFilters(this.props.filters)
            ? this.props.filters.location.bounds
            : bounds || additionalSearch?.bounds;
        const { openMoreFiltersModal, displayMap, hoveredHomeId, screenState, loading, additionalLoading } =
            this.state;

        if (!this.state.filters) {
            return null;
        }

        const isLoading = loading || additionalLoading;

        const baseResultsToShow = this.shouldShowPush(Search.MAIN_RESULTS) ? 31 : 32;
        const numResultsToShow = screenState.isLargeScreen ? baseResultsToShow + 1 : baseResultsToShow;

        return (
            <div className={classNames('row', { 'search-row-map-view': this.isMobileMapView() && isLogged })}>
                <div
                    ref={(node) => {
                        this.scrollable = node;
                    }}
                    className={classNames('col-xs-12 col-md-8 col-lg-7 search-panel', {
                        'disable-scroll': openMoreFiltersModal
                    })}
                    style={isLogged && this.isMobileMapView() ? undefined : this.getSearchPanelStyle()}
                >
                    {isLogged ? (
                        <Filters
                            ref={(filters) => {
                                this.filters = filters;
                            }}
                            user={this.props.user}
                            groups={this.props.groups}
                            filters={this.props.filters}
                            resultsBounds={bounds || additionalSearch?.bounds}
                            gpRange={this.state.gp_range}
                            updateFilters={this.updateFilters}
                            query={this.props.queryString}
                            resultsCount={this.props.resultsCount}
                            openMoreFiltersModal={openMoreFiltersModal}
                            toggleMoreFiltersModalDisplay={this.toggleMoreFiltersModalDisplay}
                            onRemoveFilter={this.removeFilter}
                            displayMap={displayMap}
                            shouldShowAdditionalResult={this.shouldShowAdditionalResults()}
                            {...(this.hasSavableLocation() && {
                                displayAlerts: this.props.displayAlerts
                            })}
                        />
                    ) : (
                        <OfflineFilters />
                    )}

                    <div className="search-scrollable col-xs-12" style={this.getSearchPanelStyle()}>
                        <div className="search-results-container" style={this.getSearchPanelStyle()}>
                            {isLoading && <HomeLoading />}

                            {!loading &&
                                this.shouldShowMainResults() &&
                                this.props.results &&
                                this.props.resultsCount > 0 && (
                                    <Flex direction="column" gap="4" className={'main-results'} isFullWidth>
                                        <Text size="lg" isBold>
                                            {this.props.resultsCount === 1 ? (
                                                <>
                                                    {i18n.t('search:nb_result_home', {
                                                        count: this.props.resultsCount
                                                    })}
                                                </>
                                            ) : (
                                                <>
                                                    {i18n.t('search:nb_result_home_plural', {
                                                        count: this.props.resultsCount
                                                    })}
                                                </>
                                            )}
                                        </Text>
                                        <Results
                                            results={
                                                results.length > numResultsToShow
                                                    ? results.slice(0, numResultsToShow)
                                                    : results
                                            }
                                            user={user}
                                            isLogged={isLogged}
                                            homeRollOver={this.homeRollOver}
                                            homeRollOut={this.homeRollOut}
                                            push={this.shouldShowPush(Search.MAIN_RESULTS)}
                                            locationQuery={queryString}
                                            searchFilters={this.props.filters}
                                            hideFixedDates={true}
                                            trackingEvent="home_card"
                                            origin="main_search"
                                            {...(!this.getExistingAlert() && {
                                                onPushAlert: this.handleOnPushAlert
                                            })}
                                        />

                                        {/* Recommendations Carousel - shown after results to show */}
                                        {!this.isFlexibleSearch(this.state.filters) &&
                                            !this.state.recommendationsLoading &&
                                            this.state.recommendations &&
                                            this.state.recommendationsTotal >= 8 &&
                                            this.page === 1 &&
                                            results.length > numResultsToShow && (
                                                <RecommendationsCarousel
                                                    recommendations={this.state.recommendations}
                                                    recommendationsTotal={this.state.recommendationsTotal}
                                                    user={user}
                                                    isLogged={isLogged}
                                                    recommendationsSeeMore={this.state.recommendationsSeeMore}
                                                />
                                            )}

                                        {/* Display remaining results if more than results to show */}
                                        {!loading &&
                                            this.shouldShowMainResults() &&
                                            this.props.results &&
                                            results.length > numResultsToShow && (
                                                <Results
                                                    results={results.slice(numResultsToShow)}
                                                    user={user}
                                                    isLogged={isLogged}
                                                    homeRollOver={this.homeRollOver}
                                                    homeRollOut={this.homeRollOut}
                                                    push={false}
                                                    locationQuery={queryString}
                                                    searchFilters={this.props.filters}
                                                    hideFixedDates={true}
                                                    trackingEvent="home_card"
                                                    origin="main_search"
                                                />
                                            )}

                                        {/* Recommendations Carousel - shown at the end if there are less than results to show */}
                                        {!this.isFlexibleSearch(this.state.filters) &&
                                            !this.state.recommendationsLoading &&
                                            this.state.recommendations &&
                                            this.state.recommendationsTotal >= 8 &&
                                            this.page === 1 &&
                                            results.length <= numResultsToShow && (
                                                <RecommendationsCarousel
                                                    recommendations={this.state.recommendations}
                                                    recommendationsTotal={this.state.recommendationsTotal}
                                                    user={user}
                                                    isLogged={isLogged}
                                                    recommendationsSeeMore={this.state.recommendationsSeeMore}
                                                />
                                            )}
                                    </Flex>
                                )}

                            {!loading &&
                                this.props.results &&
                                this.props.results?.length === 0 &&
                                this.page === 1 && (
                                    <NoResults
                                        onExpandSearchArea={this.expandSearchArea}
                                        user={user}
                                        locationQuery={queryString}
                                        {...(this.state.filters?.luxury
                                            ? {
                                                  filtersWithoutLuxury: this.removeFilter
                                              }
                                            : {})}
                                        {...(this.hasSavableLocation() &&
                                            !this.getExistingAlert() && {
                                                onPushAlert: this.handleOnPushAlert
                                            })}
                                    />
                                )}

                            {!additionalLoading &&
                                this.shouldShowAdditionalResults() &&
                                this.props.additionalSearch.results &&
                                this.props.additionalSearch.results.length > 0 && (
                                    <div
                                        className={classNames('additional-results-wrapper', {
                                            'without-pagination': this.getNbPages() === 1
                                        })}
                                    >
                                        <Flex
                                            direction="column"
                                            gap="4"
                                            className="additional-results"
                                            isFullWidth
                                        >
                                            <div className="additional-results-header">
                                                <Flex direction="column" gap="2">
                                                    <Text size="lg" isBold>
                                                        <MapPin variant="unavailable" size={45} />
                                                        {i18n.t(
                                                            'search:page.search.alternativeResults.section.title'
                                                        )}
                                                    </Text>
                                                    <Text>
                                                        {i18n.t(
                                                            'search:page.search.alternativeResults.section.subheader'
                                                        )}
                                                    </Text>
                                                </Flex>
                                            </div>
                                            <Text size="lg" isBold>
                                                {i18n.t(
                                                    'search:page.search.alternativeResults.list.subheader',
                                                    {
                                                        nb: this.props.additionalSearch.resultsCount
                                                    }
                                                )}
                                            </Text>
                                            <Results
                                                results={
                                                    this.page === 1 && !this.state.showAllAdditionalResults
                                                        ? this.props.additionalSearch.results.slice(
                                                              0,
                                                              screenState.isLargeScreen ? 3 : 2
                                                          )
                                                        : this.props.additionalSearch.results
                                                }
                                                user={this.props.user}
                                                isLogged={isLogged}
                                                homeRollOver={this.homeRollOver}
                                                homeRollOut={this.homeRollOut}
                                                hideFixedDates={false}
                                                trackingEvent="home_card"
                                                origin="dual_search"
                                            />
                                            {this.props.additionalSearch.results.length >
                                                (screenState.isLargeScreen ? 3 : 2) &&
                                                this.page === 1 &&
                                                !this.state.showAllAdditionalResults && (
                                                    <Flex
                                                        className="additional-results-button"
                                                        justifyContent="center"
                                                        isFullWidth
                                                    >
                                                        <Button
                                                            hierarchy="secondary"
                                                            onClick={this.showAllAdditionalResults}
                                                        >
                                                            {i18n.t(
                                                                'search:page.search.alternativeResults.section.cta'
                                                            )}
                                                        </Button>
                                                    </Flex>
                                                )}
                                        </Flex>
                                    </div>
                                )}
                        </div>

                        {!(
                            this.shouldShowAdditionalResults() &&
                            this.props.additionalSearch.results &&
                            this.props.additionalSearch.results.length >
                                (screenState.isLargeScreen ? 3 : 2) &&
                            this.page === 1 &&
                            !this.state.showAllAdditionalResults
                        ) &&
                            this.getNbPages() > 1 && (
                                <div
                                    className={classNames('pagination-container', {
                                        'with-additional-results':
                                            this.shouldShowAdditionalResults() &&
                                            this.props.additionalSearch.results &&
                                            this.props.additionalSearch.results.length > 0
                                    })}
                                >
                                    <Pagination
                                        page={this.page}
                                        totalPages={this.getNbPages()}
                                        onPageChange={this.gotoPage}
                                        labelOf={i18n.t('of')}
                                    />
                                </div>
                            )}
                    </div>
                </div>
                {screenState.noDesktop && (
                    <div className="button-mobile-map">
                        <button onClick={this.handleClick} className="btn-map">
                            <span className="btn-map-title">
                                {i18n.t(`search:${displayMap ? 'list' : 'map'}-title-button`)}
                            </span>
                            <span>
                                {displayMap ? (
                                    <Icon name="List" color="inverted" size="2xl" />
                                ) : (
                                    <Icon name="Map" color="inverted" size="2xl" />
                                )}
                            </span>
                        </button>
                    </div>
                )}
                <div
                    ref={(node) => {
                        this.mapContainer = node;
                    }}
                    className="col-xs-12 col-md-4 col-lg-5 search-map-container"
                    style={{
                        display:
                            (Screen.noDesktop() && displayMap) || Screen.isTabletOrUpper() ? 'block' : 'none'
                    }}
                >
                    {initialBounds && (
                        <SearchMap
                            ref={this.searchMapRef}
                            results={this.props.results?.filter(
                                (result) => result.attributes.location.coordinates
                            )}
                            additionalResults={
                                this.shouldShowAdditionalResults()
                                    ? this.props.additionalSearch.results?.filter(
                                          (result) => result.attributes.location.coordinates
                                      )
                                    : []
                            }
                            initialBounds={initialBounds}
                            onBoundsChange={this.setBounds}
                            loading={loading || additionalLoading}
                            hoveredHomeId={hoveredHomeId}
                            user={user}
                            isLogged={isLogged}
                            searchFilters={this.props.filters}
                        />
                    )}
                </div>
            </div>
        );
    }
}

function mapStateToProps(state) {
    const props = {
        ...state.search.main,
        additionalSearch: { ...state.search.additional },
        user: state.user,
        lastSearchId: state.search.lastSearchId,
        groups: state.user ? state.userGroups[state.user.id] : [],
        isLogged: Boolean(state.auth.accessToken),
        alerts: state.savedSearches,
        displayAlerts: state.search.displayAlertsComponent,
        homeFavorites: state.homeFavorites,
        accessToken: state.auth.accessToken
    };

    if (props.results) {
        props.results = props.results.map((home) => new Home(home));
    }

    if (props.additionalSearch.results) {
        props.additionalSearch.results = props.additionalSearch.results.map((home) => new Home(home));
    }

    return props;
}

const mapDispatchToProps = (dispatch) => ({
    updateDisplayAlertsStatus: bindActionCreators(setDisplayAlertsComponentStatus, dispatch),
    addAlert: bindActionCreators(addSearch, dispatch),
    fetchHomeFavorites: bindActionCreators(fetchHomeFavorites, dispatch),
    fetchSavedSearches: bindActionCreators(fetchSavedSearches, dispatch),
    dispatch
});

export default connect(mapStateToProps, mapDispatchToProps)(Search);
